import { useState, useCallback } from "react";
import Modal from "../../common/components/Modal";
import BasicButton from "../../common/components/BasicButton";
import { twMerge } from "tailwind-merge";
import { DocumentIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";

interface InfohubUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
}

function InfohubUploadModal({
  open,
  onClose,
  onUpload,
}: InfohubUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file: File): string | null => {
    if (file.type !== "application/pdf") {
      return "PDF 파일만 업로드 가능합니다.";
    }
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      return "파일 크기는 100MB 이하여야 합니다.";
    }
    return null;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const validationError = validateFile(droppedFile);
      if (validationError) {
        setError(validationError);
        setFile(null);
      } else {
        setError(null);
        setFile(droppedFile);
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setError(validationError);
        setFile(null);
      } else {
        setError(null);
        setFile(selectedFile);
      }
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      await onUpload(file);
      setFile(null);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "업로드 중 오류가 발생했습니다."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  return (
    <Modal
      open={open}
      title="문서 업로드"
      width="600px"
      height="500px"
      onClose={handleClose}
    >
      <div className="flex h-full flex-col gap-4">
        {/* Drag and Drop Area */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={twMerge(
            "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors",
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50",
            !file && "cursor-pointer hover:border-gray-400"
          )}
        >
          {!file ? (
            <>
              <CloudArrowUpIcon className="mb-4 h-16 w-16 text-gray-400" />
              <p className="mb-2 text-lg font-semibold text-gray-700">
                파일을 드래그하거나 클릭하여 선택
              </p>
              <p className="mb-4 text-sm text-gray-500">
                PDF 파일만 업로드 가능 (최대 100MB)
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <span className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
                  파일 선택
                </span>
              </label>
            </>
          ) : (
            <div className="flex w-full items-center gap-4">
              <DocumentIcon className="h-12 w-12 flex-shrink-0 text-blue-600" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  setError(null);
                }}
                className="text-sm font-medium text-red-600 hover:text-red-700"
                disabled={uploading}
              >
                제거
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Upload Info */}
        <div className="rounded-md bg-blue-50 p-3">
          <p className="text-xs text-blue-700">
            업로드 후 문서는 자동으로 처리됩니다. 100페이지 기준 약 5~10분이
            소요됩니다.
          </p>
        </div>

        {/* Buttons */}
        <div className="mt-auto flex justify-end gap-2">
          <BasicButton
            text="취소"
            variant="outlined"
            onClick={handleClose}
            disabled={uploading}
          />
          <BasicButton
            text="업로드"
            onClick={handleSubmit}
            disabled={!file || uploading}
            loading={uploading}
          />
        </div>
      </div>
    </Modal>
  );
}

export default InfohubUploadModal;
